import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CallActivityTracesState, useCallActivityTraces } from '../../hooks/useCallActivityTraces';
import {
  formatTraceDuration,
  formatTraceTimestamp,
  getDefaultDateRange,
  getTraceDisplayName,
} from './call-activity-log.utils';
import { CallActivityTraceDetails } from './CallActivityTraceDetails';
import { CallDirectionBadge } from '../call/CallDirectionBadge';
import { formatPhoneForDisplay } from '../../utils/format-phone.util';
import styles from './CallActivityLogPanel.module.css';

interface CallActivityLogPanelProps {
  enabled?: boolean;
  traces?: CallActivityTracesState;
}

export function CallActivityLogPanel({ enabled = true, traces }: CallActivityLogPanelProps) {
  const { t } = useTranslation('activityLog');
  const internalTraces = useCallActivityTraces({ enabled: traces ? false : enabled });
  const {
    fromDate,
    toDate,
    callerSearch,
    items,
    total,
    hasMore,
    isLoading,
    error,
    expandedTraceId,
    pageStart,
    pageEnd,
    applyFilters,
    clearFilters,
    goToPreviousPage,
    goToNextPage,
    toggleExpanded,
  } = traces ?? internalTraces;

  const [draftFromDate, setDraftFromDate] = useState(fromDate);
  const [draftToDate, setDraftToDate] = useState(toDate);
  const [draftCallerSearch, setDraftCallerSearch] = useState(callerSearch);

  useEffect(() => {
    setDraftFromDate(fromDate);
    setDraftToDate(toDate);
    setDraftCallerSearch(callerSearch);
  }, [fromDate, toDate, callerSearch]);

  const handleFilterSubmit = (event: FormEvent) => {
    event.preventDefault();
    applyFilters(draftFromDate, draftToDate, draftCallerSearch);
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  const handleClearCallerSearch = () => {
    setDraftCallerSearch('');
    applyFilters(draftFromDate, draftToDate, '');
  };

  const defaultRange = getDefaultDateRange();
  const hasActiveFilters =
    callerSearch.trim().length > 0 ||
    fromDate !== defaultRange.fromDate ||
    toDate !== defaultRange.toDate;

  return (
    <section className={styles.panel}>
      <div className={styles.panelBody}>
        <form className={styles.filters} onSubmit={handleFilterSubmit}>
          <label className={styles.filterField}>
            <span>{t('fromDate')}</span>
            <input
              type="date"
              value={draftFromDate}
              onChange={(event) => setDraftFromDate(event.target.value)}
            />
          </label>
          <label className={styles.filterField}>
            <span>{t('toDate')}</span>
            <input
              type="date"
              value={draftToDate}
              onChange={(event) => setDraftToDate(event.target.value)}
            />
          </label>
          <label className={`${styles.filterField} ${styles.callerSearchField}`}>
            <span>{t('callerSearch')}</span>
            <div className={styles.searchInputWrap}>
              <input
                type="search"
                value={draftCallerSearch}
                onChange={(event) => setDraftCallerSearch(event.target.value)}
                placeholder={t('callerSearchPlaceholder')}
                aria-label={t('callerSearchPlaceholder')}
              />
              {draftCallerSearch && (
                <button
                  type="button"
                  className={styles.searchClearButton}
                  onClick={handleClearCallerSearch}
                  aria-label={t('clearSearch')}
                  title={t('clearSearch')}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              )}
            </div>
          </label>
          <div className={styles.filterActions}>
            <button type="submit" className={styles.filterButton} disabled={isLoading}>
              {t('applyFilters')}
            </button>
            <button
              type="button"
              className={styles.filterButtonSecondary}
              onClick={handleClearFilters}
              disabled={isLoading || !hasActiveFilters}
            >
              {t('clearFilters')}
            </button>
          </div>
        </form>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.listContainer}>
          {isLoading ? (
            <div className={styles.stateMessage}>{t('loading')}</div>
          ) : items.length === 0 ? (
            <div className={styles.stateMessage}>{t('empty')}</div>
          ) : (
            <ul className={styles.traceList}>
              {items.map((trace) => {
                const isTraceExpanded = expandedTraceId === trace.callTraceId;
                const displayName = getTraceDisplayName(trace);
                const duration = formatTraceDuration(trace.startedAt, trace.endedAt);

                return (
                  <li key={trace.callTraceId} className={styles.traceItem}>
                    <button
                      type="button"
                      className={styles.traceSummaryButton}
                      onClick={() => toggleExpanded(trace.callTraceId)}
                      aria-expanded={isTraceExpanded}
                    >
                      <div className={styles.traceMain}>
                        <div className={styles.traceNameRow}>
                          <span className={styles.traceName}>{displayName}</span>
                          <CallDirectionBadge direction={trace.direction} />
                        </div>
                        <span className={styles.traceMeta}>
                          {trace.phone ? formatPhoneForDisplay(trace.phone) : t('unknownDirection')}
                        </span>
                      </div>
                      <div className={styles.traceAside}>
                        <span className={styles.traceTime}>
                          {formatTraceTimestamp(trace.startedAt)}
                        </span>
                        {duration && (
                          <span className={styles.traceDuration}>
                            {t('duration', { duration })}
                          </span>
                        )}
                        {trace.dispositionName && (
                          <span className={styles.traceDisposition}>{trace.dispositionName}</span>
                        )}
                        {trace.selectedContactName && (
                          <span className={styles.traceContact}>{trace.selectedContactName}</span>
                        )}
                      </div>
                    </button>

                    {isTraceExpanded && (
                      <div className={styles.traceDetails}>
                        <CallActivityTraceDetails callTraceId={trace.callTraceId} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {total > 0 && (
          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>
              {t('pagination', { start: pageStart, end: pageEnd, total })}
            </span>
            <div className={styles.paginationActions}>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={goToPreviousPage}
                disabled={isLoading || pageStart <= 1}
              >
                {t('previous')}
              </button>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={goToNextPage}
                disabled={isLoading || !hasMore}
              >
                {t('next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
